import { LanguageService } from './../language.service';
import { TriggerbeeRequester, TriggerbeeApi, ParameterType } from './../triggerbee/triggerbee-requester.service';
import { Logger, LoggerFactory } from './../logger.service';
import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { IdentityService } from './identity.service';
import { CurrentContextService } from '../current-context.service';
import { UserModel } from 'app/account-settings/models/user.model';

// Posts the browser's language/timezone to the user record on first login if missing.
@Injectable()
export class ResourceFillerService {
  private readonly logger: Logger;
  private hasFilled = false;
  private readonly destroyRef = inject(DestroyRef);

  constructor(
    loggerFactory: LoggerFactory,
    private readonly identityService: IdentityService,
    private readonly currentContextService: CurrentContextService,
    private readonly languageService: LanguageService,
    private readonly requester: TriggerbeeRequester,
  ) {
    this.logger = loggerFactory.get('ResourceFillerService');
  }

  public async initializeAsync(): Promise<void> {
    this.currentContextService.currentContextBS.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(async (context) => {
      if (!this.hasFilled && context?.user) {
        this.hasFilled = true;
        await this.fillAsync(context.user);
      }
    });
  }

  private async fillAsync(user: UserModel) {
    if (!this.identityService.isUserVerified /* as the endpoints are protected for the verified users */) {
      return;
    }
    await this.ensureCultureResourcesExistAsync(user);
  }

  private async ensureCultureResourcesExistAsync(user: UserModel) {
    await Promise.all([this.ensureLanguageExists(user), this.ensureTimeZoneExists(user)]);
  }

  private async ensureLanguageExists(user: UserModel): Promise<boolean> {
    if (user.language) {
      return false;
    }
    const language = await this.languageService.getCurrentLanguageAsync();
    if (!language) {
      this.logger.error('Could not read current language. Update failed');
      return false;
    }
    this.logger.debug(`No language is set on user. Posting current language : ${language} to add it.`);
    const response = await this.requester.postAsync(
      TriggerbeeApi.Gateway,
      'authentication/resources/language',
      { language: language },
      ParameterType.FormUrlEncoded,
    );
    if (!response.isPositive()) {
      this.logger.error('Response does not indicate success. Could not update users language.');
      return false;
    }
    this.logger.info(`Missing user language is set to ${language}`);
    return true;
  }

  private async ensureTimeZoneExists(user: UserModel): Promise<boolean> {
    if (user.timeZoneId) {
      return false;
    }
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (!timeZone) {
      this.logger.error('Time zone could not be found');
      return false;
    }
    const response = await this.requester.postAsync(
      TriggerbeeApi.Gateway,
      'authentication/resources/time-zone',
      { timeZone: timeZone },
      ParameterType.FormUrlEncoded,
    );
    if (!response.isPositive()) {
      this.logger.error('Api has failed to set the time zone');
      return false;
    }
    this.logger.info(`Missing timezone is set to ${timeZone}`);
    return true;
  }
}
